<template>
  <form novalidate :disabled="loadingData" @submit.prevent="() => {}">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200px" flat color="transparent">
          <v-card-title>
            <FormHeader
              :loading="loading"
              :link="'page-dictionaries'"
              :title="getTitle ? `${getTitle} справочник` : `Создать справочник`"
            />
          </v-card-title>
          <v-tabs v-model="activeTab">
            <v-tab v-for="tab in $options.tabs" :key="tab.value">
              {{ tab.title }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <v-card-text>
                <PageContent>
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <span>Имя *</span>
                        <v-text-field
                        id="name"
                          v-model="fields.name"
                          outlined
                          :autofocus="!fields.name"
                          :error-messages="validationErrors.name"
                          :disabled="loading"
                        />
                      </v-col>
                      <v-col cols="12">
                        <span>Метка *</span>
                        <v-text-field
                        id="label"
                          v-model="fields.label"
                          outlined
                          :disabled="loading"
                          :error-messages="validationErrors.label"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </PageContent>
              </v-card-text>
            </v-tab-item>
            <v-tab-item>
              <PageContent>
                <DynamicListWrapper
                  :multiple-creating="false"
                  :list="fields.dictionaryOptions"
                  :error-messages="mixValidationErrors"
                  :available-items="$options.availableDictionaryOptions"
                  :copy-clipboard="mixCopyListItemClipboard"
                  :options="$options.options"
                  @list-sort="mixListItemsReassort"
                  @add="mixCreateListItemHandler"
                  @option="mixListItemOptionHandler"
                  @option-manual="optionPointHandler"
                >
                  <template #item="{ data }">
                    <component
                      :is="data.item.type"
                      :list-item="data.item"
                      @change="mixListItemChangeHandler($event, data.itemIndex)"
                      @change-item="mixListItemChangeItemHandler($event, data.itemIndex)"
                      @remove-item="mixListItemRemoveItemHandler($event, data.itemIndex)"
                    />
                  </template>
                </DynamicListWrapper>
              </PageContent>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          v-if="isNew"
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="isNew"
          :is-loading="loading"
          :language="language"
          :translations="fields.translations"
          :sortOrder="fields.sortOrder"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @cancel="$router.push({ name: 'page-dictionaries' })"
          @submit="submit"
          @update-sort-order="fields.sortOrder = Number($event)"
        />
        <ManageSidePanel
          v-else-if="data"
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="!data.id"
          :created-at="data.createdAt"
          :updated-at="data.updatedAt"
          :is-loading="loading"
          :language="language"
          :translations="fields.translations"
          :entity-id="data.id"
          :entity-type="'dictionary'"
          :sortOrder="fields.sortOrder"
          @delete="$emit('delete-dictionary')"
          @cancel="$router.push({ name: 'page-dictionaries' })"
          @submit="submit"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @update-sort-order="fields.sortOrder = Number($event)"
        />
      </v-col>
    </v-row>
  </form>
</template>
<script>
import validationRules from './validation-rules.js'
import FormAbstract from '@/components/abstract/form'
import ManageSidePanel from '@/components/manage-side-panel'
import { dictionaryOptionsList } from '@/utils/widgets'
import { DictionaryOption } from '@/utils/widgets/widgetsTypes'
import DynamicListWrapper from '@/components/dynamic-list-wrapper'
import parentDynamicList from '@/mixins/dynamic-list/parentDynamicList.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'
import dictionaryOptionsParentMix from '@/mixins/dictionaryOptions/dictionaryOptionsParent.mix.js'
import Widget from '@/utils/widgets/Widget'
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import FormHeader from '@/components/form-header'
import PageContent from '@/components/page-content/PageContent.vue'

const tabs = [
  {
    title: 'Справочник',
    value: 'dictionary'
  },
  {
    title: 'Опции справочника',
    value: 'dictionaryOptions'
  }
]

export default {
  name: 'FormPageDictionary',
  validationRules,
  tabs,
  availableDictionaryOptions: dictionaryOptionsList,
  dynamicListName: 'dictionaryOptions',
  mixins: [
    parentDynamicList,
    dictionaryOptionsParentMix,
    validationTranslations,
    prepareTranslations
  ],
  components: {
    PageContent,
    ManageSidePanel,
    DynamicListWrapper,
    FormHeader
  },
  extends: FormAbstract,
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    loadingData: {
      type: Boolean,
      default: false
    },
    language: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      activeTab: 0,
      fields: {
        name: null,
        label: null,
        dictionaryOptions: [],
        translations: { ru: { isEnabled: true } },
        sortOrder: null
      },
      postActions: {
        itemsToRemove: []
      }
    }
  },
  computed: {
    getTitle () {
      if (this.data) {
        return this.data?.label || ''
      }
      return null
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.preparePoints(data.dictionaryOptions)
    }
  },
  methods: {
    resetPostActions () {
      this.postActions.itemsToRemove = []
    },
    optionPointHandler ({ value }, itemIndex, item) {
      // TODO: attention! hardcoded value in the cases
      switch (value) {
        case 'delete':
          this.postActions.itemsToRemove.push(item.data.id)
          this.mixListItemRemoveItem(itemIndex)
          break
      }
    },
    preparePoints (points) {
      this.fields.dictionaryOptions = points.map(point => {
        const preparedPoint = {
          data: { ...point }
        }
        return Widget.populate(preparedPoint, DictionaryOption)
      })
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      const postActions = { ...this.postActions }
      this.resetPostActions()
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      this.mixPrepareTranslations(payload)
      this.$emit('submit', {
        data: payload,
        postActions
      })
    }
  }
}
</script>
